import { AutocompleteFormComponent } from "components/Form/components/Autocomplete/AutocompleteFormComponent";
import { getUpdatedData } from "components/Form/components/SchemaForm/SchemaFormService";
import { SchemaFieldTypeExtended } from "config/config";
import { CustomFilterValues } from "containers/AdvancedSearch/AdvancedSearch";
import { ValueLabelPair } from "containers/ExecutionFilter/interfaces";
import { useMatchingFields } from "containers/Filter/FilterBuilder/useMatchingFields";
import { useEffect, useMemo, useState } from "react";
import { SingleField } from "utils/utils";
import { getMatchingSchema, getOperatorList, isBooleanTypeOperator } from "../utils";

interface ExtendedFilterFieldFragmentProps {
    input: any;
    meta: any;
    matchingSchema: SchemaFieldTypeExtended | null;
    fieldNameIndex: number;
    isRoot?: boolean;
    elementData: SingleField;
    setData: (value: CustomFilterValues) => void;
    filteredFieldList?: ValueLabelPair[];
    matchingFields: SchemaFieldTypeExtended[];
    formData: CustomFilterValues;
    elementPath: string;
    setCurrentMatchingSchema: any;
    partialPath: string;
}

/**
 * Component for rendering extended/nested filter fields.
 * Handles complex field relationships where a field can reference another table,
 * allowing for nested field selection (e.g., "user:profile:name").
 *
 * This component recursively renders itself when dealing with extended fields
 * that have foreign key relationships to other tables.
 *
 * @param {ExtendedFilterFieldFragmentProps} props - Component props
 * @returns {JSX.Element} Rendered extended filter field
 */
export const ExtendedFilterFieldFragment = ({
    input,
    meta,
    matchingSchema,
    fieldNameIndex,
    isRoot,
    elementData,
    setData,
    filteredFieldList,
    matchingFields,
    formData,
    elementPath,
    setCurrentMatchingSchema,
    partialPath,
}: ExtendedFilterFieldFragmentProps) => {
    // Parse the field path into an array (e.g., "user:profile:name" => ["user", "profile", "name"])
    const valuesArray = useMemo(() => elementData.field?.split(":") ?? [], [elementData.field]);

    // Current field value for this level of nesting
    const [currentFieldValue, setCurrentFieldValue] = useState(valuesArray[fieldNameIndex] ?? null);

    // Get nested fields for the foreign key table if this is an extended field
    const { filteredFieldList: nestedFieldFilteredList, matchingFields: nestedMatchingFields } =
        useMatchingFields({
            activeTab: matchingSchema?.foreign_keys?.[0]?.table ?? "",
        });

    // Get the matching schema for the next level of nesting
    const nestedMatchingSchema = useMemo(
        () =>
            getMatchingSchema(nestedMatchingFields, valuesArray[fieldNameIndex + 1] ?? "") ?? null,
        [fieldNameIndex, nestedMatchingFields, valuesArray]
    );

    // Update current field value when element data changes
    useEffect(() => {
        setCurrentFieldValue(valuesArray[fieldNameIndex] ?? null);
    }, [elementData.field, fieldNameIndex, valuesArray]);

    // Update the current matching schema when nested schema changes (but only if not extended)
    useEffect(() => {
        if (!nestedMatchingSchema?.isExtended) {
            setCurrentMatchingSchema(nestedMatchingSchema);
        }
    }, [nestedMatchingSchema, setCurrentMatchingSchema]);

    return (
        <div className="nestedFilterField" style={{ marginRight: isRoot ? "16px" : "0px" }}>
            {/* Autocomplete dropdown for field selection */}
            <AutocompleteFormComponent
                fieldProps={{
                    label: "",
                    options: {
                        list: filteredFieldList,
                    },
                    customKey: `${input.name}-${elementData.field}`,
                    fieldWidth: 240,
                }}
                input={{
                    ...input,
                    name: `${partialPath}[${fieldNameIndex}]`,
                    onChange: (value) => {
                        // Get the matching schema for the selected value
                        const newMatchingSchema = getMatchingSchema(matchingFields, value);
                        const copiedValuesArray = [...valuesArray];

                        // Handle field value replacement logic:
                        // - If changing an existing value, replace current and remove all subsequent values
                        // - If adding a new value, simply append it to the array
                        if (copiedValuesArray[fieldNameIndex] !== undefined) {
                            // Replace current value and remove all subsequent values (up to 10 levels deep)
                            copiedValuesArray.splice(fieldNameIndex, 10, value);
                        } else {
                            // Add new value to the array
                            copiedValuesArray.push(value);
                        }
                        const newFieldValue = copiedValuesArray.join(":");

                        // Handle extended fields (fields with foreign key relationships)
                        if (newMatchingSchema?.isExtended) {
                            // For extended fields, reset operator and value since we need to select the next field
                            setData(
                                getUpdatedData(formData, elementPath, {
                                    field: newFieldValue,
                                    op: null,
                                    value: null,
                                })
                            );
                        } else {
                            // For regular fields, set default operator and value based on field type
                            const defaultOp =
                                getOperatorList(newMatchingSchema)?.[0]?.value ?? null;

                            /**
                             * Get default value based on field type
                             * @returns {any} Default value for the field type
                             */
                            const getDefaultValue = () => {
                                if (newMatchingSchema?.type === "str") {
                                    // For string fields, check if it's an enum or foreign key
                                    if (
                                        newMatchingSchema.foreign_keys?.length ||
                                        newMatchingSchema.schema?.["enum"]
                                    ) {
                                        return null; // Will be handled by dropdown
                                    } else {
                                        return ""; // Empty string for text input
                                    }
                                } else if (newMatchingSchema?.type === "bool") {
                                    return true; // Default to true for boolean
                                } else if (newMatchingSchema?.type === "int") {
                                    return 0; // Default to 0 for integers
                                }
                                return null; // Default for other types
                            };

                            // Update form data with new field, operator, and value
                            setData(
                                getUpdatedData(formData, elementPath, {
                                    field: newFieldValue,
                                    op: isBooleanTypeOperator(defaultOp) ? "eq" : defaultOp,
                                    value: getDefaultValue(),
                                })
                            );
                        }

                        // Update current matching schema and field value
                        setCurrentMatchingSchema(newMatchingSchema);
                        setCurrentFieldValue(value);
                    },
                    value: currentFieldValue,
                }}
                meta={meta}
                fieldAttr={{}}
            />

            {/* Recursively render next level if current field is extended */}
            {matchingSchema?.isExtended && (
                <ExtendedFilterFieldFragment
                    input={input}
                    meta={meta}
                    matchingSchema={nestedMatchingSchema}
                    fieldNameIndex={fieldNameIndex + 1}
                    elementData={elementData}
                    elementPath={elementPath}
                    setData={setData}
                    formData={formData}
                    matchingFields={nestedMatchingFields}
                    filteredFieldList={nestedFieldFilteredList}
                    setCurrentMatchingSchema={setCurrentMatchingSchema}
                    partialPath={partialPath}
                />
            )}
        </div>
    );
};
